/**
 * HTTP client built on `ky` (fetch-based, smaller than axios) with an axios-like `api` surface
 * so server actions keep the same call pattern: `response.data`, `AxiosError`, `isAxiosError`.
 */
import ky, { HTTPError, type Options } from "ky";
import { getSessionApiKeySync } from "@/lib/cookies";

/** Minimal config shape previously passed to axios helpers */
export type AxiosRequestConfig = {
  params?: Record<string, string | number | boolean | undefined | null>;
  headers?: Record<string, string | undefined>;
};

export interface ErrorResponseData {
  hasError: boolean;
  errorCode: number | null;
  traceId: number;
  message: string;
}

export interface AxiosCompatibleError<T = ErrorResponseData> extends Error {
  isAxiosError: true;
  /** Present when the server returned a body (axios-compatible). */
  response?: {
    status: number;
    statusText: string;
    data: T;
    headers: Headers;
  };
  /** Optional: set when no HTTP response (e.g. network failure); kept for axios-style checks. */
  request?: unknown;
  config?: { url?: string; baseURL?: string; method?: string };
}

export type AxiosError<T = ErrorResponseData> = AxiosCompatibleError<T>;

export function isAxiosError(payload: unknown): payload is AxiosError {
  return (
    typeof payload === "object" &&
    payload !== null &&
    "isAxiosError" in payload &&
    (payload as AxiosCompatibleError).isAxiosError === true
  );
}

/** Prefer import.meta.env (Vite client); define also injects process.env.NEXT_PUBLIC_API_URL at build time. */
function resolveApiBaseUrl(): string {
  const env = import.meta.env;
  const fromMeta =
    env.VITE_PUBLIC_API_URL?.trim() ||
    env.VITE_API_URL?.trim() ||
    env.NEXT_PUBLIC_API_URL?.trim() ||
    "";
  const fromProcess =
    typeof process !== "undefined" &&
    typeof process.env !== "undefined" &&
    (process.env.NEXT_PUBLIC_API_URL || process.env.API_URL)
      ? String(process.env.NEXT_PUBLIC_API_URL || process.env.API_URL).trim()
      : "";
  return (fromMeta || fromProcess).replace(/\/$/, "");
}

const rawBase = resolveApiBaseUrl();
const apiRoot = rawBase ? `${rawBase}/api` : "/api";
/** Ky v2 uses `baseUrl`; trailing slash recommended when the path extends the API root. */
const baseUrl = apiRoot.endsWith("/") ? apiRoot : `${apiRoot}/`;

function normalizePath(url: string): string {
  return url.startsWith("/") ? url.slice(1) : url;
}

/** Do not attach session token to unauthenticated account endpoints. */
function shouldAttachAuthToken(normalizedPath: string): boolean {
  const p = normalizedPath.split("?")[0] ?? normalizedPath;
  const publicPrefixes = [
    "accounts/login",
    "accounts/register",
    "accounts/resend-verification",
    "accounts/forgot-password",
  ];
  return !publicPrefixes.some((prefix) => p.startsWith(prefix));
}

function mergeHeaders(
  config?: AxiosRequestConfig,
  bodyIsFormData?: boolean,
): Record<string, string> {
  const h: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (config?.headers) {
    for (const [k, v] of Object.entries(config.headers)) {
      if (v !== undefined && v !== null) h[k] = v;
    }
  }
  if (bodyIsFormData) {
    delete h["Content-Type"];
  }
  return h;
}

function buildSearchParams(
  params?: AxiosRequestConfig["params"],
): Record<string, string | number | boolean> | undefined {
  if (!params) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    out[k] = typeof v === "boolean" || typeof v === "number" ? v : String(v);
  }
  return Object.keys(out).length ? out : undefined;
}

async function parseBody<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }
  const ct = response.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    const text = await response.text();
    if (!text.length) return undefined as T;
    return JSON.parse(text) as T;
  }
  return (await response.text()) as T;
}

async function toAxiosError(error: HTTPError): Promise<AxiosError> {
  let data: ErrorResponseData | undefined;
  try {
    const ct = error.response.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      const text = await error.response.text();
      if (text.length) {
        data = JSON.parse(text) as ErrorResponseData;
      }
    }
  } catch {
    /* ignore */
  }

  if (import.meta.env.DEV && data) {
    console.log({
      ERROR: "ERROR",
      url: error.request?.url,
      message: data.message,
      traceId: data.traceId,
    });
  }

  const message = `${data?.message ?? error.message}${
    import.meta.env.DEV && data?.traceId
      ? ` (${data.traceId})`
      : ""
  }`;

  const err = new Error(message) as AxiosCompatibleError;
  err.isAxiosError = true;
  err.response = {
    status: error.response.status,
    statusText: error.response.statusText,
    data: data as ErrorResponseData,
    headers: error.response.headers,
  };
  err.config = { url: error.request?.url };

  if (error.response.status === 403 && typeof window !== "undefined") {
    localStorage.removeItem("token");
    localStorage.removeItem("x-api-key");
    sessionStorage.removeItem("token");
    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
  }

  return err;
}

const kyInstance = ky.create({
  baseUrl,
  timeout: 30000,
  retry: 0,
});

async function request<T>(
  method: string,
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<{
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
  config: Record<string, unknown>;
}> {
  const path = normalizePath(url);
  const bodyIsFormData = data instanceof FormData;
  const headers = mergeHeaders(config, bodyIsFormData);

  /**
   * مصدر واحد للجلسة في المتصفح (كوكي + localStorage عبر `getSessionApiKeySync`).
   * يجب أن يطغى على `Authorization` القادم من server actions التي تستخدم `getCookieByName` فقط،
   * وإلا يبقى كوكي قديم/فارغ فيمنع إرسال المفتاح الصحيح من localStorage.
   */
  if (typeof document !== "undefined" && shouldAttachAuthToken(path)) {
    const token = getSessionApiKeySync();
    if (token) {
      headers.Authorization = token;
      headers["X-API-KEY"] = token;
    }
  }

  const options: Options = {
    method,
    headers,
    searchParams: buildSearchParams(config?.params),
  };

  if (data !== undefined) {
    if (data instanceof FormData) {
      options.body = data;
    } else {
      options.json = data as Record<string, unknown>;
    }
  }

  try {
    const response = await kyInstance(path, options);
    const parsed = await parseBody<T>(response);
    return {
      data: parsed,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      config: {},
    };
  } catch (error) {
    if (error instanceof HTTPError) {
      throw await toAxiosError(error);
    }
    throw error;
  }
}

export const api = {
  get: <T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<{
    data: T;
    status: number;
    statusText: string;
    headers: Headers;
    config: Record<string, unknown>;
  }> => request<T>("GET", url, undefined, config),

  post: <T = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig,
  ) => request<T>("POST", url, data, config),

  put: <T = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig,
  ) => request<T>("PUT", url, data, config),

  delete: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    request<T>("DELETE", url, undefined, config),

  patch: <T = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig,
  ) => request<T>("PATCH", url, data, config),
};
